import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import Images from "assets/images"
import DriveImages from "assets/driveImages"
import {
    STORAGE_AWS,
    STORAGE_GOOGLE,
    STORAGE_MICROSOFT,
    LIST, BLOCKS,
} from "const"
import { deepCopy } from "client/utils"
import {
    DRIVE_ACTION_RENAME,
    DRIVE_ACTION_DELETE,
    DRIVE_ACTION_CLEAR_RECYCLE,
    DRIVE_ACTION_RESTORE,
    DRIVE_ACTION_MOVE,

    StoreType,
    DriveFolder,
    DriveFile,
    DriveMenuPlacement,

    setDriveFolderActive,
    setDriveFolderSelected,
    setDriveFileActive,
    setDriveFileSelected,
    setDrivePopupMenu,
    setDrivePopupMenuShow,
    setDriveAction,

    createFileName,
    createFolderName,
    isRecycleOpened,
    clearDriveSelection,
    getFileLogo,
    convertFileSize,
    addModal,
} from "client/store"
import {
    Link,
    NewFileIcon,
    NewFolderIcon,
    NoDataIcon,
    AmazonIcon,
    GoogleIcon,
    OneDriveIcon,
    Logo,
    Bars,
    EllipsisIcon,
    DateFormatter,
    UserIcon,
} from "client/components"

import FileAdd from "client/pages/drive/fileAdd"

import {
    DriveContentWrapper,
    ElementSize,
    MenuButton,
    CheckButton,
    ContextMenuTarget,

    ElementWrapper,
    ElementLogo,
    ElementName,
    ElementIconWrapper,

    ListHeaderWrapper,
    ListHeaderCheckbox,
    ListHeaderLogo,
    ListHeaderTitle,
    ListHeaderOwner,
    ListHeaderModified,
    ListHeaderSize,
    ListHeaderMenu,

    ListRowWrapper,
    ListRowCheckbox,
    ListRowLogo,
    ListRowLogoImage,
    ListRowTitle,
    ListRowOwner,
    ListRowModified,
    ListRowSize,
    ListRowMenu,
    ListFolderIconWrapper,
    ListFileIconWrapper,

    EmptyListWrapper,
    EmptyListTitle,
    EmptyListButtonWrapper,
    EmptyListButton,
} from "./styled"

import PopupMenu from "./popupMenu"
import Toolbar from "../toolbar"
import Rename from "../actions/rename"
import { AddFolder } from "../actions/add"
import { OpenFile, OpenFolder } from "../actions/open"
import ClearRecycleConfirm from "../actions/clearRecycle"
import RemoveConfirm from "../actions/remove"
import RestoreConfirm from "../actions/restore"
import MoveConfirm from "../actions/move"

const DriveContent = () => {
    const drivePath = useSelector((state: StoreType) => state.drive.drivePath)
    const baseRoute = useSelector((state: StoreType) => state.drive.baseRoute)
    const listStyle = useSelector((state: StoreType) => state.drive.listStyle)
    const action = useSelector((state: StoreType) => state.drive.action)

    const onSelectFile = useSelector((state: StoreType) => state.drive.onSelectFile)
    const onSelectFiles = useSelector((state: StoreType) => state.drive.onSelectFiles)
    const onSelectVersion = useSelector((state: StoreType) => state.drive.onSelectVersion)
    const onSelect = onSelectFile || onSelectFiles || onSelectVersion

    const folderList = useSelector((state: StoreType) => state.drive.folder.folderList)
    const folderListEmpty = useSelector((state: StoreType) => state.drive.folder.emptyList)
    const folderActive = useSelector((state: StoreType) => state.drive.folder.active)
    const foldersSelected = useSelector((state: StoreType) => state.drive.folder.selected)

    const fileList = useSelector((state: StoreType) => state.drive.file.fileList)
    const fileListEmpty = useSelector((state: StoreType) => state.drive.file.emptyList)
    const fileActive = useSelector((state: StoreType) => state.drive.file.active)
    const filesSelected = useSelector((state: StoreType) => state.drive.file.selected)

    const userList = useSelector((state: StoreType) => state.user.userList)
    const userSettings = useSelector((state: StoreType) => state.system.userSettings)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()
    const [size, setSize] = useState<ElementSize>(null)

    const defaultLogo = userSettings.project && userSettings.project.defaultLogo

    useEffect(() => {
        fixSize()
        window.addEventListener("resize", fixSize)
        window.addEventListener("click", clearSelected)
        return () => {
            window.removeEventListener("resize", fixSize)
            window.removeEventListener("click", clearSelected)

            clearDriveSelection(dispatch)
        }
    }, [])

    const fixSize = () => {
        const driveList = document.body.querySelector(DriveContentWrapper)
        const width = driveList.clientWidth

        if (width < 960) setSize("xs")
        if (width >= 960 && width < 1200) setSize("sm")
        if (width >= 1200 && width < 1440) setSize("md")
        if (width >= 1440 && width < 1680) setSize("lg")
        if (width >= 1680) setSize("xl")
    }

    const clearSelected = (e: MouseEvent) => {
        const element = e.target as Element
        if (!element.closest(ElementWrapper)) {
            clearDriveSelection(dispatch)
        }
    }

    const selectFolder = (folder: DriveFolder) => {
        dispatch(setDriveFolderActive(null))
        dispatch(setDriveFileActive(null))

        if (foldersSelected.find((obj) => obj.id === folder.id)) {
            const newSelected = foldersSelected.filter((obj) => obj.id !== folder.id)
            dispatch(setDriveFolderSelected(newSelected))
        } else {
            const newSelected: DriveFolder[] = deepCopy(foldersSelected)
            newSelected.push(folder)
            dispatch(setDriveFolderSelected(newSelected))
        }
    }

    const selectFile = (file: DriveFile) => {
        dispatch(setDriveFolderActive(null))
        dispatch(setDriveFileActive(null))

        if (filesSelected.find((obj) => obj.id === file.id)) {
            const newSelected = filesSelected.filter((obj) => obj.id !== file.id)
            dispatch(setDriveFileSelected(newSelected))
        } else {
            const newSelected: DriveFile[] = deepCopy(filesSelected)
            newSelected.push(file)
            dispatch(setDriveFileSelected(newSelected))
        }
    }

    const clickFolder = (e: React.MouseEvent, folder: DriveFolder) => {
        e.stopPropagation()
        if (filesSelected.length || foldersSelected.length) {
            // project and QID folders can't be selected
            if (folder.project || folder.product) return
            selectFolder(folder)
            return
        }

        const target = e.target as Element
        if (target.nodeName === "INPUT") return // rename in progress

        if (target.closest(CheckButton)) {
            selectFolder(folder)
        } else {
            dispatch(setDriveFolderActive(folder))
            dispatch(setDriveFileActive(null))
        }

        dispatch(setDriveAction(null))
    }

    const clickFile = (e: React.MouseEvent, file: DriveFile) => {
        e.stopPropagation()
        if (filesSelected.length || foldersSelected.length) {
            selectFile(file)
            return
        }

        const target = e.target as Element
        if (target.nodeName === "INPUT") return // rename in progress

        if (target.closest(CheckButton)) {
            selectFile(file)
        } else {
            dispatch(setDriveFileActive(file))
            dispatch(setDriveFolderActive(null))
        }

        dispatch(setDriveAction(null))
    }

    const showContextMenu = (
        e: React.MouseEvent,
        folder: DriveFolder,
        file: DriveFile,
    ) => {
        e.preventDefault()
        e.stopPropagation()

        const target = e.currentTarget as HTMLElement
        const targetCoords = target.getBoundingClientRect()
        let placement: DriveMenuPlacement

        if (e.type === "contextmenu") { // context menu with right click
            const contextTarget = target.querySelector(ContextMenuTarget) as HTMLElement

            const left = e.clientX - targetCoords.left
            const top = e.clientY - targetCoords.top
            contextTarget.style.left = `${left}px`
            contextTarget.style.top = `${top}px`

            if (window.innerWidth - e.clientX < 200) placement = "left"
            else placement = "right"
            dispatch(setDrivePopupMenu(contextTarget, placement))
            dispatch(setDrivePopupMenuShow(false))
            setTimeout(() => {
                dispatch(setDrivePopupMenuShow(true))
            }, 0)
        }

        if (e.type === "click") { // menu click with left button
            if (window.innerWidth - targetCoords.right < 200) placement = "left"
            else placement = "right"
            dispatch(setDrivePopupMenu(target, placement))
            dispatch(setDrivePopupMenuShow(true))
        }

        dispatch(setDriveFolderActive(folder))
        dispatch(setDriveFileActive(file))

        dispatch(setDriveFolderSelected([]))
        dispatch(setDriveFileSelected([]))

        dispatch(setDriveAction(null))
    }

    const singleFolderSelected = foldersSelected.length === 1 && filesSelected.length === 0
    const singleFileSelected = foldersSelected.length === 0 && filesSelected.length === 1

    const recycleOpened = isRecycleOpened(drivePath)
    const showEmpty = folderListEmpty && fileListEmpty

    return (
        <DriveContentWrapper>
            {
                (folderActive || fileActive || foldersSelected.length > 0 || filesSelected.length > 0)
                && <Toolbar />
            }
            {
                !showEmpty && listStyle === LIST && (
                    <ListHeaderWrapper>
                        <ListHeaderCheckbox />
                        <ListHeaderLogo />
                        <ListHeaderTitle>{lang.name}</ListHeaderTitle>
                        <ListHeaderOwner>{lang.owner}</ListHeaderOwner>
                        <ListHeaderModified>{lang.modified}</ListHeaderModified>
                        <ListHeaderSize>{lang.size}</ListHeaderSize>
                        <ListHeaderMenu />
                    </ListHeaderWrapper>
                )
            }
            {
                folderList.map((folder) => {
                    const key = `${folder.id}_${folder.name}`
                    const name = createFolderName(folder)
                    const active = folderActive && folderActive.id === folder.id
                    const selected = !!foldersSelected.find((obj) => obj.id === folder.id)

                    const rename = action === DRIVE_ACTION_RENAME && (
                        (folderActive && folderActive.id === folder.id)
                        || (singleFolderSelected && foldersSelected[0].id === folder.id)
                    )

                    if (listStyle === BLOCKS) {
                        return (
                            <ElementWrapper
                                key={key}
                                size={size}
                                selected={active || selected}
                                onClick={(e) => clickFolder(e, folder)}
                                onDoubleClick={() => OpenFolder(folder, userData, onSelect, dispatch)}
                                onContextMenu={(e) => showContextMenu(e, folder, null)}
                            >
                                <ContextMenuTarget />
                                <MenuButton blockView onClick={(e) => showContextMenu(e, folder, null)}>
                                    <Bars />
                                </MenuButton>
                                {
                                    !folder.project
                                    && !folder.product
                                    && <CheckButton blockView checked={selected} />
                                }

                                <ElementLogo src={DriveImages.folder} />
                                <ElementName offsetRight={!!folder.project || !!folder.product}>
                                    <Link
                                        href={onSelect ? "/#" : folder.url}
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            OpenFolder(folder, userData, onSelect, dispatch)
                                        }}
                                    >
                                        {name}
                                    </Link>
                                </ElementName>
                                {
                                    rename && <Rename />
                                }
                                {
                                    folder.project && (
                                        <ElementIconWrapper title={folder.project.name}>
                                            <Logo
                                                logo={folder.project.logo || defaultLogo || Images.noImage}
                                                size={26}
                                                borderRadius={13}
                                            />
                                        </ElementIconWrapper>
                                    )
                                }
                                {
                                    folder.product && (
                                        <ElementIconWrapper title={folder.product.name}>
                                            <Logo
                                                logo={folder.product.logo || Images.qidProductLogo}
                                                size={26}
                                                borderRadius={13}
                                            />
                                        </ElementIconWrapper>
                                    )
                                }
                            </ElementWrapper>
                        )
                    }

                    if (listStyle === LIST) {
                        return (
                            <ListRowWrapper
                                key={key}
                                selected={active || selected}
                                onClick={(e) => clickFolder(e, folder)}
                                onDoubleClick={() => OpenFolder(folder, userData, onSelect, dispatch)}
                                onContextMenu={(e) => showContextMenu(e, folder, null)}
                            >
                                <ContextMenuTarget />
                                <ListRowCheckbox>
                                    {
                                        !folder.project
                                        && !folder.product
                                        && <CheckButton listView checked={selected} />
                                    }
                                </ListRowCheckbox>
                                <ListRowLogo>
                                    <ListRowLogoImage src={DriveImages.folder} />
                                    {
                                        folder.project && (
                                            <ListFolderIconWrapper title={folder.project.name}>
                                                <Logo
                                                    logo={folder.project.logo || defaultLogo || Images.noImage}
                                                    size={24}
                                                    borderRadius={12}
                                                />
                                            </ListFolderIconWrapper>
                                        )
                                    }
                                    {
                                        folder.product && (
                                            <ListFolderIconWrapper title={folder.product.name}>
                                                <Logo
                                                    logo={folder.product.logo || Images.qidProductLogo}
                                                    size={24}
                                                    borderRadius={12}
                                                />
                                            </ListFolderIconWrapper>
                                        )
                                    }
                                </ListRowLogo>
                                <ListRowTitle>
                                    <Link
                                        href={onSelect ? "/#" : folder.url}
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            OpenFolder(folder, userData, onSelect, dispatch)
                                        }}
                                    >
                                        {name}
                                    </Link>
                                    {
                                        rename && <Rename />
                                    }
                                </ListRowTitle>
                                <ListRowOwner />
                                <ListRowModified><DateFormatter date={folder.updatedAt} /></ListRowModified>
                                <ListRowSize>—</ListRowSize>
                                <ListRowMenu onClick={(e) => showContextMenu(e, folder, null)}>
                                    <MenuButton><EllipsisIcon /></MenuButton>
                                </ListRowMenu>
                            </ListRowWrapper>
                        )
                    }

                    return null
                })
            }
            {
                fileList.map((file) => {
                    const key = `${file.id}_${file.name}`
                    const name = createFileName(file)
                    const active = fileActive && fileActive.id === file.id
                    const selected = !!filesSelected.find((obj) => obj.id === file.id)

                    const rename = action === DRIVE_ACTION_RENAME && (
                        (fileActive && fileActive.id === file.id)
                        || (singleFileSelected && filesSelected[0].id === file.id)
                    )

                    if (listStyle === BLOCKS) {
                        return (
                            <ElementWrapper
                                key={key}
                                size={size}
                                selected={active || selected}
                                onClick={(e) => clickFile(e, file)}
                                onDoubleClick={() => OpenFile(file.id, file.url, drivePath, baseRoute, onSelect, dispatch)}
                                onContextMenu={(e) => showContextMenu(e, null, file)}
                            >
                                <ContextMenuTarget />
                                <MenuButton blockView onClick={(e) => showContextMenu(e, null, file)}>
                                    <Bars />
                                </MenuButton>
                                <CheckButton blockView checked={selected} />

                                <ElementLogo src={getFileLogo(file, file.versions && file.versions[0])} />
                                <ElementName offsetRight>
                                    <Link
                                        href={onSelect ? "/#" : file.url}
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            OpenFile(file.id, file.url, drivePath, baseRoute, onSelect, dispatch)
                                        }}
                                    >
                                        {name}
                                    </Link>
                                </ElementName>
                                {
                                    rename && <Rename />
                                }
                                <ElementIconWrapper title={lang[file.storage]}>
                                    { file.storage === STORAGE_AWS && <AmazonIcon /> }
                                    { file.storage === STORAGE_GOOGLE && <GoogleIcon /> }
                                    { file.storage === STORAGE_MICROSOFT && <OneDriveIcon /> }
                                </ElementIconWrapper>
                            </ElementWrapper>
                        )
                    }

                    if (listStyle === LIST) {
                        // summ of all versions size
                        let calculatedSize = 0
                        if (file.versions && file.versions.length) {
                            file.versions.forEach((version) => {
                                calculatedSize += version.size
                            })
                        }
                        const fileSize = file.versions && file.versions.length ? convertFileSize(calculatedSize) : null

                        const owner = userList[file.owner]

                        return (
                            <ListRowWrapper
                                key={key}
                                selected={active || selected}
                                onClick={(e) => clickFile(e, file)}
                                onDoubleClick={() => OpenFile(file.id, file.url, drivePath, baseRoute, onSelect, dispatch)}
                                onContextMenu={(e) => showContextMenu(e, null, file)}
                            >
                                <ContextMenuTarget />
                                <ListRowCheckbox>
                                    <CheckButton listView checked={selected} />
                                </ListRowCheckbox>
                                <ListRowLogo>
                                    <ListRowLogoImage src={getFileLogo(file, file.versions && file.versions[0])} />
                                    <ListFileIconWrapper title={lang[file.storage]}>
                                        { file.storage === STORAGE_AWS && <AmazonIcon /> }
                                        { file.storage === STORAGE_GOOGLE && <GoogleIcon /> }
                                        { file.storage === STORAGE_MICROSOFT && <OneDriveIcon /> }
                                    </ListFileIconWrapper>
                                </ListRowLogo>
                                <ListRowTitle>
                                    <Link
                                        href={onSelect ? "/#" : file.url}
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            OpenFile(file.id, file.url, drivePath, baseRoute, onSelect, dispatch)
                                        }}
                                    >
                                        {name}
                                    </Link>
                                    {
                                        rename && <Rename />
                                    }
                                </ListRowTitle>
                                <ListRowOwner>
                                    <UserIcon user={owner} size="sm" />
                                </ListRowOwner>
                                <ListRowModified>
                                    <DateFormatter date={file.updatedAt} />
                                </ListRowModified>
                                <ListRowSize>{fileSize || "—"}</ListRowSize>
                                <ListRowMenu onClick={(e) => showContextMenu(e, null, file)}>
                                    <MenuButton><EllipsisIcon /></MenuButton>
                                </ListRowMenu>
                            </ListRowWrapper>
                        )
                    }

                    return null
                })
            }

            {
                !recycleOpened && showEmpty && (
                    <EmptyListWrapper>
                        <EmptyListTitle>{lang.emptyFolder}</EmptyListTitle>
                        <EmptyListButtonWrapper>
                            <EmptyListButton onClick={() => dispatch(addModal({ key: "addFile", content: <FileAdd /> }))}>
                                <NewFileIcon />
                                {lang.addFile_s}
                            </EmptyListButton>
                            <EmptyListButton onClick={() => AddFolder(drivePath, baseRoute, userData, folderList, lang, dispatch)}>
                                <NewFolderIcon />
                                {lang.createFolder}
                            </EmptyListButton>
                        </EmptyListButtonWrapper>
                    </EmptyListWrapper>
                )
            }
            {
                recycleOpened && showEmpty && (
                    <EmptyListWrapper>
                        <NoDataIcon />
                    </EmptyListWrapper>
                )
            }

            <PopupMenu />

            {
                action === DRIVE_ACTION_DELETE
                && <RemoveConfirm />
            }
            {
                action === DRIVE_ACTION_CLEAR_RECYCLE
                && <ClearRecycleConfirm />
            }
            {
                action === DRIVE_ACTION_RESTORE
                && <RestoreConfirm />
            }
            {
                action === DRIVE_ACTION_MOVE
                && <MoveConfirm />
            }
        </DriveContentWrapper>
    )
}
export default DriveContent
